import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { useAuth } from '@entities/auth';
import { hasPermission } from '@entities/access';
import { swrKeys } from '@shared/api/swr/keys';
import { useApiServices } from '@shared/lib/use-api-services';
import type { SidebarItem } from '../model/sidebar.types';

export function useSidebarConfig(): SidebarItem[] {
  const location = useLocation();
  const { t } = useTranslation('cabinet');
  const { user } = useAuth();
  const showWallets = hasPermission(user, 'wallet.read');
  const showAccounting = hasPermission(user, 'accounting.read_any');
  const showAudit = hasPermission(user, 'audit.read');
  const showReconciliation = hasPermission(user, 'reconciliation.read');
  const showWithdrawalControl = hasPermission(user, 'platform.manage');
  const isAdmin = user?.isAdmin === true || user?.role === 'ADMIN';
  const showBilling = hasPermission(user, 'aml.billing.manage');
  const services = useApiServices();
  const queue = useSWR(
    isAdmin ? swrKeys.compliance.queue({ page: 1, limit: 1 }) : null,
    () => services.compliance.listWithdrawals({ page: 1, limit: 1 }),
    { refreshInterval: 20000 },
  );

  const items: SidebarItem[] = [
    {
      id: 'balance',
      label: t('sidebar.balance'),
      icon: 'wallet',
      href: '/balance',
      active: location.pathname === '/' || location.pathname === '/balance',
    },
    {
      id: 'payments',
      label: t('sidebar.payments'),
      icon: 'receipt',
      href: '/payments',
      active: location.pathname === '/payments' || location.pathname.startsWith('/payments/'),
    },
    {
      id: 'projects',
      label: t('sidebar.projects'),
      icon: 'folders',
      href: '/projects',
      active: location.pathname === '/projects' || location.pathname === '/projects/add',
    },
    {
      id: 'withdrawals',
      label: t('sidebar.withdrawals'),
      icon: 'money-from-bracket',
      href: '/withdrawals',
      active: location.pathname === '/withdrawals',
    },
    {
      id: 'referrals',
      label: t('sidebar.referrals'),
      icon: 'user-plus',
      href: '/referrals',
      active: location.pathname === '/referrals',
    },
  ];

  if (showWallets) {
    items.push({
      id: 'wallets',
      label: t('sidebar.wallets'),
      icon: 'landmark',
      href: '/wallets',
      active: location.pathname === '/wallets',
    });
  }

  if (showAccounting) {
    items.push({
      id: 'service-accounting',
      label: t('sidebar.serviceAccounting'),
      icon: 'calculator',
      href: '/service-accounting',
      active: location.pathname === '/service-accounting',
    });
  }

  if (showWithdrawalControl) {
    items.push({
      id: 'withdrawal-control',
      label: t('sidebar.withdrawalControl'),
      icon: 'shield-alert',
      href: '/withdrawal-control',
      active: location.pathname === '/withdrawal-control',
    });
  }

  if (isAdmin) {
    items.push({
      id: 'compliance',
      label: t('sidebar.compliance'),
      icon: 'shield-check',
      href: '/compliance',
      active: location.pathname === '/compliance',
      badge: queue.data?.pagination.total,
    });
    items.push({
      id: 'aml-settings',
      label: t('sidebar.amlSettings'),
      icon: 'sliders',
      href: '/aml-settings',
      active: location.pathname === '/aml-settings',
    });
  }

  if (showBilling) {
    items.push({
      id: 'aml-billing',
      label: t('sidebar.amlBilling'),
      icon: 'circle-dollar',
      href: '/aml-billing',
      active: location.pathname === '/aml-billing',
    });
  }

  if (showAudit) {
    items.push({
      id: 'audit-log',
      label: t('sidebar.auditLog'),
      icon: 'scroll-text',
      href: '/audit-log',
      active: location.pathname === '/audit-log',
    });
  }

  if (showReconciliation) {
    items.push({
      id: 'reconciliation',
      label: t('sidebar.reconciliation'),
      icon: 'scale',
      href: '/reconciliation',
      active: location.pathname === '/reconciliation',
    });
  }

  if (isAdmin) {
    items.unshift({
      id: 'workspace',
      label: t('sidebar.workspace'),
      icon: 'objects-column',
      href: '/workspace',
      active: location.pathname === '/workspace',
    });
  }

  return items;
}
